import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser';
import { USERHUB_DEV_TOKEN, USERHUB_SCOPES } from '../userhub/config';

/**
 * Microsoft Entra ID sign-in for the SPA (auth-code + PKCE — a browser app is a PUBLIC client and
 * never holds the registration's client secret).
 *
 * Configuration is RUNTIME, not build-time: the SPA is a static bundle on Azure Static Web Apps
 * serving every environment, so tenant/client ids arrive via public/config.js
 * (window.__NIOBE_CONFIG__.auth), the same channel as the backend URL. When that block is absent
 * — local dev — auth is considered "not configured" and the app keeps using the dev stub
 * identity, so `npm start` works with no Entra round-trip. See lib/session.ts.
 */

export interface AuthConfig {
  tenantId: string;
  clientId: string;
  /** Scope the SPA requests for the backend API, e.g. "api://<clientId>/access_as_user". */
  scope: string;
}

export function authConfig(): AuthConfig | null {
  // Same precedence as api.ts#backendUrl: runtime config first (prod, public/config.js), then a
  // build-time env var so a developer can exercise the REAL Entra flow locally via .env.local.
  const c = window.__NIOBE_CONFIG__?.auth;
  const tenantId = c?.tenantId ?? import.meta.env.VITE_AUTH_TENANT_ID ?? '';
  const clientId = c?.clientId ?? import.meta.env.VITE_AUTH_CLIENT_ID ?? '';
  if (!tenantId || !clientId) return null;
  return {
    tenantId,
    clientId,
    scope: c?.scope ?? import.meta.env.VITE_AUTH_SCOPE ?? `api://${clientId}/access_as_user`,
  };
}

/** What the shell's `niobe:auth-token` / `niobe:auth-signin` IPC answers. */
interface HostTokenReply {
  token?: string;
  account?: AccountInfo;
  error?: string;
  /**
   * Why there is no token, from a shell that classifies it (client/desktop/host-auth.js):
   * 'transient' is the network (wait and retry), 'session_expired' means the member must sign in
   * again. An older shell sends no code.
   */
  code?: string;
}

/**
 * Sign-in brokered by the DESKTOP SHELL instead of by this page.
 *
 * Present only inside Niobe Desktop, and only when it has host auth enabled. Its presence IS the
 * instruction to use it — there is no flag to read and no way for the two paths to disagree.
 *
 * WHY IT EXISTS. A token minted inside Electron's embedded Chromium carries no device-compliance
 * claim: unlike Edge, it has no access to the OS Primary Refresh Token. Everything works with such
 * a token except the one thing that re-evaluates Conditional Access — the backend's on-behalf-of
 * exchange for a Graph token — so SharePoint bid-site browsing, and nothing else, failed in the
 * desktop app with AADSTS53000 while working perfectly in a browser tab. The shell signs in through
 * the member's REAL browser and hands the resulting token in here.
 *
 * The identity is unchanged: same tenant, same app registration, same `access_as_user` scope. Only
 * the window it was obtained in differs.
 */
interface HostAuthBridge {
  /** `{ interactive: false }` is a background renewal: the shell never opens the browser for it. */
  getToken(opts?: { interactive?: boolean }): Promise<HostTokenReply>;
  /** Open the browser now — the member clicked "Sign in". Absent on an older shell. */
  signIn?(): Promise<HostTokenReply>;
  signOut(): Promise<unknown>;
  /**
   * A Graph `User.Read` token for the userhub feedback widget — never the `access_as_user` one.
   * Silent unless `{ interactive: true }` on a shell that sets `graphInteractive`: then, when Entra
   * refuses the Graph refresh, the shell runs its browser sign-in. Absent on an older shell.
   */
  getGraphToken?(opts?: { interactive?: boolean }): Promise<HostTokenReply>;
  /** The shell honours `getGraphToken({ interactive: true })`. An older shell ignores the option. */
  graphInteractive?: boolean;
}

function hostAuth(): HostAuthBridge | null {
  return (window as { niobeDesktop?: { auth?: HostAuthBridge } }).niobeDesktop?.auth ?? null;
}

/** The last account the host reported, so `currentAccount()` stays synchronous for render paths. */
let hostAccount: AccountInfo | null = null;

/**
 * The desktop shell said the session is over: Entra rejected the refresh token during a BACKGROUND
 * renewal. The shell no longer opens a browser tab on its own for that (it used to, on every
 * failure, network ones included). The page shows "Your session has expired — Sign in" instead,
 * and the browser opens when the member clicks. Never set in a plain browser.
 */
let sessionExpired = false;
const sessionListeners = new Set<() => void>();

function setSessionExpired(next: boolean): void {
  if (sessionExpired === next) return;
  sessionExpired = next;
  for (const l of [...sessionListeners]) l();
}

export function isSessionExpired(): boolean {
  return sessionExpired;
}

export function subscribeSessionExpired(cb: () => void): () => void {
  sessionListeners.add(cb);
  return () => {
    sessionListeners.delete(cb);
  };
}

/**
 * Sign-in could not reach Entra — the network is not up yet (a launch at login, a wake). Not a
 * failure to show: AuthGate waits and tries again. Falling back to MSAL here would navigate the
 * window to an Entra page that cannot load either.
 */
export class TransientSignInError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TransientSignInError';
  }
}

/** A token out of a host reply, or null — noting a session the shell reports as expired. */
function fromHost(res: HostTokenReply | undefined): AuthResult | null {
  if (res?.token && res.account) {
    hostAccount = res.account;
    setSessionExpired(false);
    return { token: res.token, account: res.account };
  }
  if (res?.code === 'session_expired') setSessionExpired(true);
  return null;
}

/** Ask the host, turning an IPC throw into "no reply" (an older shell, a renderer shutting down). */
async function askHost(call: () => Promise<HostTokenReply>): Promise<HostTokenReply | undefined> {
  try {
    return await call();
  } catch (e) {
    console.warn('[auth] desktop host sign-in threw', e);
    return undefined;
  }
}

/**
 * A token from the shell, or null to fall back to MSAL.
 *
 * Null on almost any failure — the shell not being there, the browser sign-in being cancelled, the
 * loopback port being busy. Sign-in is the one thing that must never become impossible: a member
 * who cannot browse SharePoint has one broken pane, a member who cannot sign in has no app at all.
 * The exception is the network: a 'transient' reply throws TransientSignInError, so AuthGate waits
 * for the network instead of switching sign-in paths.
 */
async function hostSignIn(): Promise<AuthResult | null> {
  const host = hostAuth();
  if (!host) return null;
  const res = await askHost(() => host.getToken());
  const hosted = fromHost(res);
  if (hosted) return hosted;
  if (res?.code === 'transient') throw new TransientSignInError(res.error ?? 'the network is not available');
  console.warn('[auth] desktop host sign-in unavailable (%s) — falling back to MSAL', res?.error ?? 'no token');
  return null;
}

/**
 * The member clicked "Sign in" on the session-expired banner: the shell opens the browser. Null
 * when it did not complete (cancelled, timed out) — the banner stays and they can try again.
 */
export async function signInWithHost(): Promise<AuthResult | null> {
  const host = hostAuth();
  if (!host) return null;
  // An older shell has no signIn(), and its getToken() is interactive anyway.
  return fromHost(await askHost(() => (host.signIn ? host.signIn() : host.getToken())));
}

export function isAuthConfigured(): boolean {
  // The host brokers sign-in itself, so the page needs no tenant/client id of its own to be
  // "configured". Without this, a desktop build whose config.js was somehow missing would silently
  // drop to the dev stub identity and 401 against the real backend.
  return hostAuth() !== null || authConfig() !== null;
}

let instance: PublicClientApplication | null = null;
let initialized = false;

// One-shot guard: set before we recover a silent failure with an interactive redirect, cleared on
// the next successful token acquisition. Stops a hard misconfiguration from looping redirect→
// timeout→redirect forever (a genuine failure then surfaces as an error instead).
const SILENT_FALLBACK_KEY = 'niobe.auth.silentFallback';

async function client(cfg: AuthConfig): Promise<PublicClientApplication> {
  if (!instance) {
    const configuration: Configuration = {
      auth: {
        clientId: cfg.clientId,
        authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
        // The DEFAULT redirect URI — used by the silent-renewal hidden iframe (acquireTokenSilent).
        // A minimal blank page, NOT the app root: loading the full SPA in the iframe re-runs
        // AuthGate/signIn there and drops the #code before MSAL reads it, so silent times out.
        // Interactive sign-in overrides this back to the app root per-request (see signIn below).
        // Both `${origin}/blank.html` AND `${origin}` must be registered SPA redirect URIs.
        redirectUri: `${window.location.origin}/blank.html`,
        postLogoutRedirectUri: window.location.origin,
      },
      cache: {
        // sessionStorage keeps the session per-tab, matching how the dev stub behaves and
        // avoiding a shared login leaking across tabs on a shared machine.
        cacheLocation: 'sessionStorage',
      },
    };
    instance = new PublicClientApplication(configuration);
  }
  if (!initialized) {
    await instance.initialize();
    initialized = true;
  }
  return instance;
}

export interface AuthResult {
  token: string;
  account: AccountInfo;
}

/**
 * Complete any in-flight redirect, then return a usable access token — triggering an interactive
 * redirect when silent acquisition is not possible. Resolves to null only while a redirect is
 * navigating away (the page is about to unload, so callers should simply render nothing).
 */
export async function signIn(): Promise<AuthResult | null> {
  // The desktop shell first, when it is there. It returns null on any failure, so this is an
  // upgrade of the sign-in path rather than a replacement of it.
  const hosted = await hostSignIn();
  if (hosted) return hosted;

  const cfg = authConfig();
  if (!cfg) return null;
  const msal = await client(cfg);

  // Handles the #code= fragment when Entra redirects back. Returns null on a normal load.
  const redirectResult = await msal.handleRedirectPromise();

  // Fresh return from an interactive redirect already carries a usable access token for the
  // requested scope — use it directly and never touch the hidden silent-renewal iframe (which
  // times out in browsers that partition third-party frames; see acquireTokenSilent below). This
  // is the primary sign-in path.
  if (redirectResult?.accessToken && redirectResult.account) {
    msal.setActiveAccount(redirectResult.account);
    sessionStorage.removeItem(SILENT_FALLBACK_KEY);
    return { token: redirectResult.accessToken, account: redirectResult.account };
  }

  const account = redirectResult?.account ?? msal.getAllAccounts()[0] ?? null;

  if (!account) {
    // Interactive sign-in returns to the APP root (not the blank silent-redirect page), so the
    // user lands back on the running SPA.
    await msal.loginRedirect({ scopes: [cfg.scope], redirectUri: window.location.origin });
    return null; // navigating away
  }

  msal.setActiveAccount(account);

  try {
    const result = await msal.acquireTokenSilent({ scopes: [cfg.scope], account });
    sessionStorage.removeItem(SILENT_FALLBACK_KEY);
    return { token: result.accessToken, account };
  } catch (e) {
    // Interaction genuinely required, OR the silent hidden-iframe timed out (partitioned/sandboxed
    // third-party frame — https://aka.ms/msal.js.errors#timed_out): recover with a full-page
    // interactive redirect instead of hard-failing sign-in. The return leg lands in the
    // redirectResult branch above. The one-shot guard keeps a hard misconfiguration from looping.
    const recoverable =
      e instanceof InteractionRequiredAuthError ||
      (e instanceof BrowserAuthError && e.errorCode === BrowserAuthErrorCodes.timedOut);
    if (recoverable && sessionStorage.getItem(SILENT_FALLBACK_KEY) !== '1') {
      sessionStorage.setItem(SILENT_FALLBACK_KEY, '1');
      await msal.acquireTokenRedirect({ scopes: [cfg.scope], account, redirectUri: window.location.origin });
      return null; // navigating away
    }
    throw e;
  }
}

/** Silent refresh for the periodic top-up. Returns null when interaction would be required. */
export async function refreshToken(): Promise<string | null> {
  // The host caches a refresh token, so its getToken() IS the silent top-up. `interactive: false`
  // makes sure no browser ever opens from here: a network failure comes back as 'transient' and a
  // revoked session as 'session_expired', which raises the banner (see `sessionExpired`).
  const host = hostAuth();
  if (host) return fromHost(await askHost(() => host.getToken({ interactive: false })))?.token ?? null;

  const cfg = authConfig();
  if (!cfg || !instance || !initialized) return null;
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  if (!account) return null;
  try {
    const result = await instance.acquireTokenSilent({ scopes: [cfg.scope], account });
    return result.accessToken;
  } catch {
    return null;
  }
}

/**
 * What the feedback dialog shows when only a fresh sign-in can produce its Graph token and this
 * shell cannot run one for it (AB#365).
 */
export const USERHUB_FRESH_SIGN_IN_MESSAGE =
  'Your Microsoft session needs a fresh sign-in — sign out and back in.';

/** The page holds a user gesture (a click in the feedback dialog), so interaction may start. */
function hasUserGesture(): boolean {
  return !navigator.userActivation || navigator.userActivation.isActive;
}

/**
 * The token the userhub feedback widget sends (src/userhub/): a Microsoft GRAPH token with
 * `User.Read`, which userhub checks with `GET /me` — not Niobe's `access_as_user` token. Same app
 * registration, same signed-in account; `User.Read` is admin-consented, so this is silent.
 *
 *  1. Niobe Desktop with host auth: the shell redeems its refresh token for Graph. Entra can refuse
 *     it for Graph alone (AADSTS70043: a sign-in-frequency policy on Graph, while the Niobe scope
 *     still redeems). With a user gesture the shell is asked again INTERACTIVELY: its browser
 *     sign-in mints a fresh refresh token. An older shell that cannot do that gets a clear
 *     "sign out and back in" message. Desktop NEVER opens the MSAL popup: popups are denied in its
 *     window, and the `about:blank` it opens first surfaced as a Windows "can't open the 'about'
 *     link" dialog (AB#365). MSAL is still tried silently (a shell with no `getGraphToken`).
 *  2. MSAL: silent, then a POPUP when interaction is required. Never a redirect — it would throw
 *     away the report the member is typing. The widget also calls this on page load (the My
 *     tickets count), so the popup is only tried while the page holds a user gesture; without one
 *     the call fails instead of opening anything.
 *  3. Stub auth (dev, the daemon's test env): a placeholder the local userhub API ignores.
 */
export async function getUserhubToken(): Promise<string> {
  if (!isAuthConfigured()) return USERHUB_DEV_TOKEN;

  const host = hostAuth();
  if (host?.getGraphToken) {
    const res = await askHost(() => host.getGraphToken!());
    if (res?.token) return res.token;
    if (res?.code === 'session_expired' && hasUserGesture()) {
      if (!host.graphInteractive) throw new Error(USERHUB_FRESH_SIGN_IN_MESSAGE);
      const again = await askHost(() => host.getGraphToken!({ interactive: true }));
      if (again?.token) return again.token;
      throw new Error(again?.error ? `Sign-in did not complete: ${again.error}` : USERHUB_FRESH_SIGN_IN_MESSAGE);
    }
    console.warn('[auth] desktop host Graph token unavailable (%s) — trying MSAL', res?.error ?? 'no token');
  }

  const cfg = authConfig();
  if (!cfg) throw new Error('no Graph token for feedback: sign-in is not configured in this page');
  const msal = await client(cfg);
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  try {
    if (!account) throw new InteractionRequiredAuthError('no_account', 'no MSAL account in this page');
    return (await msal.acquireTokenSilent({ scopes: USERHUB_SCOPES, account })).accessToken;
  } catch (e) {
    if (!(e instanceof InteractionRequiredAuthError)) throw e;
    // No gesture, no popup: a browser would block it anyway, and page load must stay silent.
    if (!hasUserGesture()) throw e;
    if (host) throw new Error(USERHUB_FRESH_SIGN_IN_MESSAGE);
    return (await msal.acquireTokenPopup({ scopes: USERHUB_SCOPES, ...(account ? { account } : {}) })).accessToken;
  }
}

/**
 * The signed-in account, synchronously. Safe from render paths only AFTER AuthGate has completed
 * sign-in (it gates the whole app), and always null in dev where auth is not configured.
 */
export function currentAccount(): AccountInfo | null {
  if (hostAccount) return hostAccount;
  if (!instance || !initialized) return null;
  return instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
}

export async function signOut(): Promise<void> {
  const host = hostAuth();
  if (host) {
    hostAccount = null;
    setSessionExpired(false);
    // Only Niobe's own copy of the session. The browser's Entra cookie belongs to the OS session,
    // and clearing it would sign the member out of every Microsoft app on the machine.
    await host.signOut().catch(() => {});
    return;
  }
  const cfg = authConfig();
  if (!cfg || !instance) return;
  await instance.logoutRedirect({ account: instance.getActiveAccount() ?? undefined });
}
